import { addDays, diffDays } from './dates';

export type Flow = 'spotting' | 'light' | 'medium' | 'heavy';

export interface CycleDay {
  flow?: Flow;
  symptoms?: string[];
  note?: string;
}

export interface CycleData {
  periods: { start: string; end?: string }[];
  days: Record<string, CycleDay>;
}

export const SYMPTOMS = ['Cramps', 'Bloating', 'Cravings', 'Low mood', 'Irritable', 'Headache', 'Tired', 'Acne', 'Tender breasts', 'Bad sleep'];

export type PhaseId = 'menstrual' | 'follicular' | 'ovulation' | 'luteal';

export interface PhaseInfo {
  id: PhaseId;
  label: string;
  short: string;
  /** Extra calories allowed on top of the daily target in this phase. */
  kcalAdjust: number;
  food: string[];
  training: string;
  partnerHint: string;
}

// General guidance, not medical advice. Kept deliberately modest.
export const PHASES: Record<PhaseId, PhaseInfo> = {
  menstrual: {
    id: 'menstrual',
    label: 'Period',
    short: 'Iron and warmth',
    kcalAdjust: 0,
    food: [
      'Lean on iron: kırmızı mercimek, ıspanak, kıyma or dana, eggs. Add lemon or a mandalina for vitamin C so the iron absorbs.',
      'Warm meals help: mercimek or ezogelin çorbası, tarhana.',
      'Keep çay away from meals (it blocks iron); have it an hour later.',
      'Cramps: 10 g walnuts or almonds and a banana give magnesium and potassium.',
    ],
    training: 'Gentle Pilates and walking are fine and often ease cramps. Skip intense sessions if you feel drained.',
    partnerHint: 'Her period. Warm food, a hot-water bottle and patience go a long way. Take over a chore today.',
  },
  follicular: {
    id: 'follicular',
    label: 'Follicular',
    short: 'Best days for the deficit',
    kcalAdjust: 0,
    food: [
      'Energy and insulin sensitivity are usually best now: stick to the plan as written.',
      'Good week to try a new dish or a slightly smaller dinner carb portion.',
    ],
    training: 'Strongest phase: push a little harder in Pilates and add an extra walk.',
    partnerHint: 'Her high-energy days. Good time for a longer walk or an active plan together.',
  },
  ovulation: {
    id: 'ovulation',
    label: 'Ovulation',
    short: 'Steady and hydrated',
    kcalAdjust: 0,
    food: ['Keep protein high and drink an extra glass of water.', 'Appetite can dip briefly; still eat the planned snacks.'],
    training: 'Normal training. Warm up well: joints can be a bit looser around ovulation.',
    partnerHint: 'Mid-cycle. Business as usual.',
  },
  luteal: {
    id: 'luteal',
    label: 'Luteal',
    short: 'Appetite up, plan for it',
    kcalAdjust: 120,
    food: [
      'Resting energy rises a little in this phase. You have about +120 kcal today: an extra apple + 10 g walnuts, or 150 g süzme yoğurt with cinnamon.',
      'Cravings: 2 squares (10 g) of bitter chocolate are planned, not a failure.',
      'Go easy on salt, pickles and processed food to limit bloating; keep water high.',
      'Complex carbs (bulgur, oats) at dinner can help sleep and mood.',
    ],
    training: 'Energy may drop in the last days. Keep Pilates, lower the intensity if needed.',
    partnerHint: 'Her luteal phase: appetite and mood can swing. An extra snack is planned; be kind about cravings.',
  },
};

export function stats(data: CycleData): { avgCycle: number; avgPeriod: number; starts: string[] } {
  const starts = [...new Set(data.periods.map((p) => p.start))].sort();
  const gaps: number[] = [];
  for (let i = 1; i < starts.length; i++) {
    const g = diffDays(starts[i], starts[i - 1]);
    if (g >= 18 && g <= 45) gaps.push(g);
  }
  const lens = data.periods.filter((p) => p.end).map((p) => diffDays(p.end!, p.start) + 1).filter((l) => l >= 1 && l <= 10);
  const avg = (a: number[], dflt: number) => (a.length ? Math.round(a.slice(-6).reduce((s, x) => s + x, 0) / Math.min(a.length, 6)) : dflt);
  return { avgCycle: avg(gaps, 28), avgPeriod: avg(lens, 5), starts };
}

export interface PhaseResult {
  info: PhaseInfo;
  day: number;
  cycleLength: number;
  nextPeriod: string;
  predicted: boolean;
}

export function phaseOn(data: CycleData, date: string): PhaseResult | null {
  const { avgCycle, avgPeriod, starts } = stats(data);
  const last = starts.filter((s) => s <= date).pop();
  if (!last) return null;
  let day = diffDays(date, last) + 1;
  let predicted = false;
  let cycleStart = last;
  if (day > avgCycle) {
    const cycles = Math.floor((day - 1) / avgCycle);
    cycleStart = addDays(last, cycles * avgCycle);
    day = diffDays(date, cycleStart) + 1;
    predicted = true;
  }
  const period = data.periods.find((p) => p.start === last);
  const periodLen = !predicted && period?.end ? diffDays(period.end, period.start) + 1 : avgPeriod;
  const ovDay = avgCycle - 14;
  let id: PhaseId;
  if (day <= periodLen) id = 'menstrual';
  else if (day >= ovDay - 2 && day <= ovDay + 1) id = 'ovulation';
  else if (day < ovDay - 2) id = 'follicular';
  else id = 'luteal';
  return { info: PHASES[id], day, cycleLength: avgCycle, nextPeriod: addDays(cycleStart, avgCycle), predicted };
}

/** Days in the month view that are logged or predicted period days. */
export function periodDays(data: CycleData, from: string, to: string): { logged: Set<string>; predicted: Set<string> } {
  const logged = new Set<string>();
  const predicted = new Set<string>();
  const { avgCycle, avgPeriod, starts } = stats(data);
  for (const p of data.periods) {
    const end = p.end ?? addDays(p.start, avgPeriod - 1);
    for (let d = p.start; d <= end; d = addDays(d, 1)) logged.add(d);
  }
  for (const [d, v] of Object.entries(data.days)) if (v.flow) logged.add(d);
  const last = starts[starts.length - 1];
  if (last) {
    let s = addDays(last, avgCycle);
    while (s <= to) {
      for (let i = 0; i < avgPeriod; i++) {
        const d = addDays(s, i);
        if (d >= from && d <= to && !logged.has(d)) predicted.add(d);
      }
      s = addDays(s, avgCycle);
    }
  }
  return { logged, predicted };
}
